import {
  SYSVAR_RENT_PUBKEY,
  SystemProgram,
  Keypair,
  PublicKey,
  TransactionInstruction,
} from '@solana/web3.js'
import {
  TOKEN_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
} from '@solana/spl-token'
import {
  validateMnemonic as bip39validateMnemonic,
  mnemonicToSeed as bip39mnemonicToSeed,
} from 'bip39'
import bs58 from 'bs58'
import { fromSeed as bip32FromSeed } from 'bip32'

import { PRESALE_TOKEN_PROGRAM } from './pubkeys'
import { sendTxUsingWallet } from './wallet'

async function mnemonicToSeed(mnemonic) {
  if (!bip39validateMnemonic(mnemonic)) {
    throw new Error('Invalid seed phrase')
  }
  return await bip39mnemonicToSeed(mnemonic)
}

function getAccountFromSeed(seed, walletIndex, accountIndex = 0) {
  const derivedSeed = bip32FromSeed(seed).derivePath(
    `m/501'/${walletIndex}'/0/${accountIndex}`
  ).privateKey
  return Keypair.fromSeed(derivedSeed)
}

export const findAssociatedTokenAccountPublicKey = async (walletAddress) => {
  return (
    await PublicKey.findProgramAddress(
      [
        walletAddress.toBuffer(),
        TOKEN_PROGRAM_ID.toBuffer(),
        PRESALE_TOKEN_PROGRAM.toBuffer(),
      ],
      ASSOCIATED_TOKEN_PROGRAM_ID
    )
  )[0]
}

export async function createAccountFromSecret(secret) {
  // Ed25519 byte array
  if (secret.includes(',')) {
    return new Keypair(
      secret
        .replace(/ /g, '')
        .split(',')
        .map((n) => parseInt(n))
    )
  }

  // Base58 encoded Ed25519 byte array
  if (!secret.includes(' ')) {
    return Keypair.fromSecretKey(bs58.decode(secret))
  }

  // Seed phrase
  const seed = await mnemonicToSeed(secret)
  return getAccountFromSeed(seed, 6, 0)
}

export async function createAssociatedTokenAccount(
  userPublicKey,
  connection,
  signTransaction
) {
  const associatedTokenAccountPublicKey =
    await findAssociatedTokenAccountPublicKey(userPublicKey)
  const ix = new TransactionInstruction({
    programId: ASSOCIATED_TOKEN_PROGRAM_ID,
    data: Buffer.from([]),
    keys: [
      { pubkey: userPublicKey, isSigner: true, isWritable: true },
      {
        pubkey: associatedTokenAccountPublicKey,
        isSigner: false,
        isWritable: true,
      },
      { pubkey: userPublicKey, isSigner: false, isWritable: false },
      { pubkey: PRESALE_TOKEN_PROGRAM, isSigner: false, isWritable: false },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: SYSVAR_RENT_PUBKEY, isSigner: false, isWritable: false },
    ],
  })

  await sendTxUsingWallet(
    [ix],
    connection,
    null,
    [],
    userPublicKey,
    signTransaction
  )

  return associatedTokenAccountPublicKey
}
